import React, { useEffect, useRef, useState, useMemo } from "react";
import "./style.css";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { motion } from "framer-motion";

// Components
import Input from "../../Input";
import Button from "../../Button";
import InputSelect from "../../Select";
import Icone from "../../../utils/Icone";
import Loading from "../../../utils/Loading";
import CriarCracha from "../../Pdf/CriarCracha";
import CarteirinhaVertiPdf from "../../Pdf/CarteirinhaVertiPdf";

// Utilities and Services
import api from "../../../Services/api";
import { Alert } from "../../../utils/Alert";

// Assets and Styles
import {
  AnimatedField,
  Buttons,
  Card,
  Contanier,
  DropDiv2,
  Forms,
  FotoIcon,
  Header,
  Image,
  ImageContainer,
  InputFileManual,
  RemoveIcon,
  Title,
} from "./style.js";
import Foto from "../../../assets/images/user.png";

const PERFIL_OPTIONS = [
  { value: 0, label: "-" },
  { value: "Aluno", label: "Aluno" },
  { value: "Coordenador", label: "Coordenador" },
  { value: "Estagiário", label: "Estagiário" },
  { value: "Funcionário", label: "Funcionário" },
  { value: "Professor", label: "Professor" },
  { value: "Secretaria", label: "Secretaria" },
  { value: "Outro", label: "Outro" },
];

export default function GerarCracha({ gerarCracha: showGerarCracha, setGerarCracha: setShowGerarCracha }) {
  if (!showGerarCracha) return null;

  const navigate = useNavigate();
  const componentVerticalRef = useRef(null);

  // Loading states
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAlunos, setIsLoadingAlunos] = useState(false);

  // UI states
  const [isFocused, setIsFocused] = useState(false);
  const [showTurma, setShowTurma] = useState("");
  const [dataInicio, setDataInicio] = useState("");

  // Data states
  const [optionsCurso, setOptionsCurso] = useState([{ label: "-", value: "" }]);
  const [optionsConvenio, setOptionsConvenio] = useState([{ label: "-", value: "" }]);
  const [optionsDatasInicio, setOptionsDatasInicio] = useState([{ label: "-", value: "" }]);
  const [alunos, setAlunos] = useState([]);

  const dataAtual = new Date();
  const dataFormatada = format(dataAtual, "yyyy-MM-dd");

  const [formValues, setFormValues] = useState({
    nome: "",
    rm: "",
    matricula: "",
    perfil: "",
    outroPerfil: "",
    nome_curso: "",
    convenio: "",
    cor_convenio: "",
    dt_termino: dataFormatada,
    fileManual: "",
    tipoManual: "",
    fileImgManual: Foto,
  });

  // Derived states based on profile selection
  const { showRM, showMatricula, showCourseAndConvenio, showOutro } = useMemo(() => {
    const isAlunoPerfil = ["Aluno", "Estagiário"].includes(formValues.perfil);
    const isProfessorPerfil = ["Coordenador", "Professor", "Funcionário"].includes(formValues.perfil);
    const shouldShowCourseAndConvenio = ["Professor", "Coordenador", "Aluno", "Estagiário"].includes(formValues.perfil);

    return {
      showRM: isAlunoPerfil && showTurma === "unico",
      showMatricula: isProfessorPerfil,
      showCourseAndConvenio: shouldShowCourseAndConvenio,
      showOutro: formValues.perfil === "Outro",
    };
  }, [formValues.perfil, showTurma]);

  // Update form value handler
  const updateFormValue = (field, value) => {
    if (field === "perfil" && value === "Aluno") {
      setShowTurma("unico");
    }
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  // Load courses on component mount
  useEffect(() => {
    if (!showGerarCracha) return;

    api
      .get("/curso/listar?procurar")
      .then((res) => {
        const data = res.data;
        const resultsCurso = Array.from(
          new Map(data.map((value) => [value.nome, { label: value.nome, value: value.nome }])).values()
        );
        setOptionsCurso([{ label: "-", value: "" }, ...resultsCurso]);
      })
      .catch((err) => console.error(err));
  }, [showGerarCracha]);

  // Update convenios when course changes
  useEffect(() => {
    if (!showGerarCracha || !formValues.nome_curso) return;

    setFormValues((prev) => ({ ...prev, convenio: "" }));
    setDataInicio("");
    setOptionsConvenio([]);
    setOptionsDatasInicio([]);

    api
      .get(`/curso/listar-convenios?procurar=${formValues.nome_curso}`)
      .then((res) => {
        setOptionsConvenio([
          { label: "-", value: "" },
          ...res.data.map((value) => ({
            label: value.convenio,
            value: value.convenio + "|" + value.cor_convenio,
          })),
        ]);

        if (res.data.length > 0) {
          setFormValues((prev) => ({
            ...prev,
            convenio: res.data[0].convenio || "",
            cor_convenio: res.data[0].cor_convenio || "",
          }));
        }
      })
      .catch((err) => console.error(err));
  }, [formValues.nome_curso, showGerarCracha]);

  // Fetch students when convenio changes and turma mode is selected
  useEffect(() => {
    if (!showGerarCracha || showTurma !== "turma" || !formValues.nome_curso || !formValues.convenio) return;

    if (dataInicio.length < 10) {
      setIsLoading(true);
    }
    setIsLoadingAlunos(true);

    const delayDebounceFn = setTimeout(() => {
      fetchAlunos(formValues.nome_curso, formValues.convenio, dataInicio.length >= 10 ? dataInicio : null);
    }, 1000); // Reduced delay time for better UX

    return () => clearTimeout(delayDebounceFn);
  }, [formValues.convenio, showGerarCracha, showTurma, dataInicio]);

  // Fetch students data
  const fetchAlunos = async (curso, convenio, dt_inicio) => {
    try {
      const endpoint = `/aluno/listar?nome_curso=${curso}&convenio=${convenio}${
        dt_inicio ? "&dt_inicio=" + dt_inicio : ""
      }`;
      const { data } = await api.get(endpoint, { headers: { "x-limite": 10000 } });
      const alunos = data.alunos;
      if (!dt_inicio) {
        // Process and set start dates
        const resultsDatasInicio = Array.from(
          new Map(
            alunos
              ?.sort((a, b) => new Date(b.dt_inicio) - new Date(a.dt_inicio))
              ?.map((value) => {
                const dataFormatada = new Intl.DateTimeFormat("pt-BR").format(new Date(value.dt_inicio));
                return [value.dt_inicio, { label: dataFormatada, value: value.dt_inicio.split("T")[0] }];
              })
          )?.values()
        );
        setOptionsDatasInicio([{ label: "-", value: "" }, ...resultsDatasInicio]);
      } else {
        // Process and set student data
        if (!alunos.length) {
          await Alert({ tipo: "Erro", mensagem: "Nenhum aluno encontrado." });
          return;
        }

        const data = alunos.map((aluno) => ({
          ...aluno,
          dt_termino: formValues.dt_termino,
        }));
        setAlunos(data);
      }
    } catch (err) {
      console.error(err);
      Alert({ tipo: "Erro", mensagem: "Nenhum aluno encontrado." });
    } finally {
      setIsLoading(false);
      setIsLoadingAlunos(false);
    }
  };

  // Handle form validation and PDF download
  const handleDownload = async (ref) => {
    // Validate date
    const validadeDate = new Date(formValues.dt_termino).toISOString().split("T")[0];
    const hoje = new Date().toISOString().split("T")[0];

    if (validadeDate < hoje) {
      Alert({ tipo: "Erro", mensagem: "Data de validade deve ser maior que hoje!" });
      return;
    }

    // Handle turma mode
    if (showTurma === "turma") {
      if (dataInicio.length < 10) {
        Alert({ tipo: "Erro", mensagem: "Escolha uma data de inicio da turma!" });
        return;
      }

      if (alunos.length === 0) {
        Alert({ tipo: "Erro", mensagem: "Este curso não possui alunos!" });
        return;
      }

      return navigate("/gerar-cracha/pdf-turma", {
        state: {
          alunos,
          nome_curso: formValues.nome_curso,
          convenio: formValues.convenio,
          dataInicio,
          validade: formValues.dt_termino,
        },
      });
    }

    // Validate form for individual mode
    const errors = [];
    if (!formValues.nome) errors.push("Nome não preenchido");
    if (showRM && !formValues.rm) errors.push("RM não preenchido");
    if (showCourseAndConvenio && showRM && (!formValues.nome_curso || !formValues.convenio)) {
      errors.push("Selecione um curso e um convênio");
    }
    if (!formValues.fileManual) errors.push("Selecione uma imagem");
    if (!formValues.perfil || formValues.perfil === "0") errors.push("Selecione um perfil");
    if (showOutro && !formValues.outroPerfil) errors.push("Perfil não preenchido");

    if (errors.length > 0) {
      Alert({ tipo: "Erro", mensagem: errors.join(", ") });
      return;
    }

    // Generate PDF
    try {
      setIsDownloading(true);

      const data = {
        ...formValues,
        validade: formValues.dt_termino.replace(/-/g, "/"),
      };

      await api.post("/usuario/log-gerarcracha", { data });

      const element = ref.current;
      if (!element) {
        throw new Error("Elemento não encontrado para captura");
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = await html2canvas(element, {
        logging: false,
        useCORS: true,
        scale: 2,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(imgData, "PNG", 10, 10, 277, 0);
      pdf.save(`cracha ${formValues.nome}.pdf`);
    } catch (error) {
      console.error("Falha no download da carteirinha:", error);
      Alert({ tipo: "Erro", mensagem: error.response?.data?.erro || "Falha ao gerar o crachá!" });
    } finally {
      Clear();
      setIsDownloading(false);
    }
  };

  const Clear = () => {
    setFormValues(() => ({
      nome: "",
      rm: "",
      matricula: "",
      perfil: "",
      outroPerfil: "",
      nome_curso: "",
      convenio: "",
      cor_convenio: "",
      dt_termino: dataFormatada,
      fileManual: "",
      tipoManual: "",
      fileImgManual: Foto,
    }));
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e?.target?.files[0];
    if (!file) return;

    if (file.type.includes("image") || file.type.includes("pdf")) {
      setFormValues((prev) => ({
        ...prev,
        fileManual: file,
        fileImgManual: URL.createObjectURL(file),
        tipoManual: file.type,
      }));
    } else {
      Alert({ tipo: "Atenção", mensagem: "Formato não permitido!" });
    }
  };

  // Handle profile type toggle
  const toggleTurmaMode = () => {
    setShowTurma((prev) => (prev === "turma" ? "unico" : "turma"));
  };

  return (
    <Contanier>
      {isDownloading && (
        <div className="Criadivanimation">
          <motion.div
            className="Criaload"
            animate={{
              scale: [1, 2, 2, 1, 1],
              rotate: [0, 0, 180, 180, 0],
              borderRadius: ["0%", "0%", "50%", "50%", "0%"],
            }}
            transition={{
              duration: 2,
              ease: "easeInOut",
              times: [0, 0.2, 0.5, 0.8, 1],
              repeat: Infinity,
              repeatDelay: 1,
            }}
          />
        </div>
      )}

      {isDownloading ? (
        <div style={{ marginLeft: 5000, position: "absolute", zIndex: 10 }}>
          <div ref={componentVerticalRef} style={{ width: 1200, height: 900 }}>
            {formValues.perfil === "Aluno" ? (
              <CarteirinhaVertiPdf Dados={formValues} />
            ) : (
              <CriarCracha Dados={formValues} />
            )}
          </div>
        </div>
      ) : (
        <Card>
          <Header>
            <Buttons
              onClick={() => {
                Clear();
                setShowGerarCracha(false);
              }}
            >
              <Icone name="X" color="#3a3f86" size={36} />
            </Buttons>
            <Title>Gerar Crachá</Title>
          </Header>

          <Forms>
            {/* Perfil Selection */}
            <InputSelect
              Label="Perfil"
              Options={PERFIL_OPTIONS}
              Style1={{ color: "black" }}
              Style2={{
                color: "white",
                backgroundColor: "#272f5e",
                border: "1px solid black",
                borderRadius: "50px",
                width: "30vh",
              }}
              Style3={{ color: "white", backgroundColor: "#272f5e" }}
              Default={formValues.perfil}
              OnChange={(e) => {
                setFormValues((prev) => ({ ...prev, nome_curso: "", convenio: "", cor_convenio: "" }));
                updateFormValue("perfil", e.target.value);
              }}
            />

            {/* Turma/Unico Toggle */}
            <AnimatedField $show={formValues.perfil === "Aluno"}>
              <div style={{ display: "flex", gap: 10 }}>
                <Button
                  id="button"
                  padding={10}
                  borderRadius={5}
                  event={toggleTurmaMode}
                  placeholder={showTurma === "turma" ? "Unico" : "Turma"}
                />
              </div>
            </AnimatedField>

            {/* Custom Profile Field */}
            <AnimatedField $show={showOutro}>
              <Input
                width="30vh"
                color="black"
                placeholdercolor="black"
                placeholder={!isFocused ? "Digite o Perfil" : "Novo Perfil:"}
                required
                style={{ alignitems: "center" }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                event={(e) => updateFormValue("outroPerfil", e.target.value)}
              />
            </AnimatedField>

            {/* Name Field */}
            <AnimatedField $show={showTurma !== "turma"}>
              <Input
                width="30vh"
                color="black"
                placeholdercolor="black"
                placeholder="Nome"
                required
                style={{ alignitems: "center" }}
                event={(e) => updateFormValue("nome", e.target.value)}
              />
            </AnimatedField>

            {/* RM Field */}
            <AnimatedField $show={showRM}>
              <Input
                width="30vh"
                color="black"
                placeholdercolor="black"
                placeholder="RM"
                required
                style={{ alignitems: "center" }}
                event={(e) => updateFormValue("rm", e.target.value)}
                apenasNumeros
              />
            </AnimatedField>

            {/* Matricula Field */}
            <AnimatedField $show={showMatricula}>
              <Input
                width="30vh"
                color="black"
                placeholdercolor="black"
                placeholder="Matricula"
                required
                style={{ alignitems: "center" }}
                event={(e) => updateFormValue("matricula", e.target.value)}
                apenasNumeros
              />
            </AnimatedField>

            {/* Course and Convenio Selection */}
            <AnimatedField $show={showCourseAndConvenio}>
              <InputSelect
                Style1={{ color: "black" }}
                Style2={{
                  color: "white",
                  backgroundColor: "#272f5e",
                  border: "1px solid black",
                  borderRadius: "50px",
                  width: "30vh",
                }}
                Style3={{ color: "white", backgroundColor: "#272f5e" }}
                Label="Curso:"
                Options={optionsCurso}
                Default={formValues.nome_curso}
                OnChange={(e) => updateFormValue("nome_curso", e.target.value)}
              />

              <InputSelect
                Style1={{ color: "black" }}
                Style2={{
                  color: "white",
                  backgroundColor: "#272f5e",
                  border: "1px solid black",
                  borderRadius: "50px",
                  width: "30vh",
                }}
                Style3={{ color: "white", backgroundColor: "#272f5e" }}
                Label="Convênio:"
                Options={optionsConvenio}
                Default={formValues.convenio + "|" + formValues.cor_convenio}
                OnChange={(e) => {
                  updateFormValue("convenio", e.target.value.split("|")[0]);
                  updateFormValue("cor_convenio", e.target.value.split("|")[1]);
                }}
              />
            </AnimatedField>

            {/* Start Date Selection for Turma */}
            <AnimatedField $show={showTurma === "turma"}>
              <InputSelect
                Style1={{ color: "black" }}
                Style2={{
                  color: "white",
                  backgroundColor: "#272f5e",
                  border: "1px solid black",
                  borderRadius: "50px",
                  width: "30vh",
                }}
                Style3={{ color: "white", backgroundColor: "#272f5e" }}
                Label="Data inicio da turma:"
                Options={isLoading ? [{ label: "-", value: "" }] : optionsDatasInicio}
                Default={dataInicio}
                OnChange={(e) => setDataInicio(e.target.value)}
              />
            </AnimatedField>

            {/* Validity Date */}
            <Input
              placeholdercolor="black"
              placeholder={<strong>Validade do crachá:</strong>}
              Style2={{ color: "#000" }}
              type="date"
              required
              value={formValues.dt_termino}
              min={format(dataAtual, "yyyy-MM-dd")}
              event={(e) => updateFormValue("dt_termino", e.target.value)}
            />

            {/* Image Upload */}
            <AnimatedField $show={showTurma !== "turma"}>
              <DropDiv2>
                {formValues.fileManual ? (
                  <ImageContainer>
                    <RemoveIcon
                      onClick={() =>
                        setFormValues((prev) => ({
                          ...prev,
                          fileManual: "",
                          fileImgManual: "",
                          tipoManual: "",
                        }))
                      }
                    />
                    <Image src={formValues.tipoManual.includes("image") ? formValues.fileImgManual : ""} alt="Foto" />
                  </ImageContainer>
                ) : (
                  <ImageContainer>
                    <FotoIcon />
                    <InputFileManual type="file" accept="image/*" onChange={handleFileUpload} />
                  </ImageContainer>
                )}
              </DropDiv2>
            </AnimatedField>
          </Forms>

          {/* Submit Button */}
          <Button
            id="button"
            event={() => handleDownload(componentVerticalRef)}
            disable={isLoadingAlunos || isLoading}
            placeholder={
              isLoadingAlunos || isLoading ? <Loading color="#fff" /> : showTurma === "turma" ? "Ver Crachás" : "Gerar"
            }
          />
        </Card>
      )}
    </Contanier>
  );
}
